import React, { ReactElement, useMemo } from 'react';
import { Box, Typography, Link, styled } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLocation, Link as RouterLink } from 'react-router-dom';

import { DeprecatedBaseHeader } from '@localstack/ui';
import { DeprecatedBaseHeaderProps } from '@localstack/types';

import { Searchbar } from '~/components/Searchbar';
import { AppRoute } from '~/config';

const ADMIN_ROUTES: Record<string, { label: string; parent?: string }> = {
  [AppRoute.ADMIN_HOME]: { label: 'Overview' },
  [AppRoute.ADMIN_ACCOUNTS]: { label: 'Users' },
  [AppRoute.ADMIN_CREATE_ACCOUNT]: { label: 'Create User', parent: AppRoute.ADMIN_ACCOUNTS },
  [AppRoute.ADMIN_ORGANIZATIONS]: { label: 'Workspaces' },
  [AppRoute.ADMIN_EXTENSIONS]: { label: 'Extensions' },
  [AppRoute.ADMIN_ANNOUNCEMENT]: { label: 'Announcements' },
  [AppRoute.ADMIN_FEATURE_FLAGS]: { label: 'Feature Flags' },
  [AppRoute.ADMIN_ADMIN_USERS]: { label: 'Manage Access' },
};

const HeaderWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  position: 'relative',
});

const TitleContainer = styled(Box)({
  position: 'absolute',
  left: 0,
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
});

const SearchContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  '& > div': {
    maxWidth: 500,
    width: '100%',
  },
});

const ActionsContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const PageTitle = styled(Typography)({
  fontWeight: 500,
  fontSize: '1.125rem',
});

export type AdminHeaderProps = DeprecatedBaseHeaderProps;

interface BreadcrumbItem {
  label: string | JSX.Element;
  path?: string;
}

interface RouteConfig {
  label: string;
  parent?: string;
}

const buildBreadcrumbsFromRoute = (route: RouteConfig) => {
  const crumbs: BreadcrumbItem[] = [{ label: route.label }];

  if (route.parent) {
    const parent = ADMIN_ROUTES[route.parent];
    if (parent) {
      crumbs.unshift({ label: parent.label, path: route.parent });
    }
  }

  return crumbs;
};

const buildBreadcrumbsForDetailPage = (path: string, title: string | JSX.Element | undefined) => {
  const parentPath = path.split('/').slice(0, 3).join('/');
  const parentRoute = ADMIN_ROUTES[parentPath];

  if (parentRoute) {
    return [{ label: parentRoute.label, path: parentPath }, { label: title || 'Details' }];
  }

  return [{ label: 'Admin' }];
};

export const AdminHeader = ({ title, actions, trailingActions, ...rest }: AdminHeaderProps): ReactElement => {
  const location = useLocation();

  const breadcrumbs: BreadcrumbItem[] = useMemo(() => {
    const path = location.pathname;

    // Try to find exact route match first
    const exactRoute = ADMIN_ROUTES[path];
    if (exactRoute) {
      return buildBreadcrumbsFromRoute(exactRoute);
    }

    // No exact match - assume it's detail page
    return buildBreadcrumbsForDetailPage(path, title);
  }, [location.pathname, title]);

  return (
    <DeprecatedBaseHeader
      {...rest}
      title={undefined}
      actions={
        <HeaderWrapper>
          <TitleContainer>
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.path || `${crumb.label}-${index}`}>
                {index > 0 && <ChevronRightIcon fontSize="small" sx={{ mx: 0.5, color: 'text.secondary' }} />}
                {crumb.path ? (
                  <Link
                    component={RouterLink}
                    to={crumb.path}
                    color="inherit"
                    underline="hover"
                    sx={{ textDecoration: 'none' }}
                  >
                    <PageTitle>{crumb.label}</PageTitle>
                  </Link>
                ) : (
                  <PageTitle color="text.primary" noWrap>
                    {crumb.label}
                  </PageTitle>
                )}
              </React.Fragment>
            ))}
          </TitleContainer>
          <SearchContainer>
            <Searchbar />
          </SearchContainer>
          <ActionsContainer>
            {actions}
            {trailingActions}
          </ActionsContainer>
        </HeaderWrapper>
      }
    />
  );
};
